function calendarParts(value: string | Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(typeof value === "string" ? new Date(value) : value);
  const part = (type: "year" | "month" | "day") =>
    Number(parts.find((item) => item.type === type)?.value);
  return { year: part("year"), month: part("month"), day: part("day") };
}

function zonedLocalToDate(
  dateKey: string,
  time: string,
  timeZone: string,
): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);
  const desired = Date.UTC(year!, month! - 1, day!, hour!, minute!);
  let guess = desired;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date(guess));
    const read = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((item) => item.type === type)?.value);
    const observed = Date.UTC(
      read("year"),
      read("month") - 1,
      read("day"),
      read("hour"),
      read("minute"),
    );
    const correction = desired - observed;
    guess += correction;
    if (correction === 0) break;
  }
  return new Date(guess);
}

export function businessDateKey(value: string | Date, timeZone: string): string {
  const { year, month, day } = calendarParts(value, timeZone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function monthKey(value: string | Date, timeZone: string): string {
  return businessDateKey(value, timeZone).slice(0, 7);
}

export function isoWeekKey(value: string | Date, timeZone: string): string {
  const { year, month, day } = calendarParts(value, timeZone);
  const localDate = new Date(Date.UTC(year, month - 1, day));
  const weekday = localDate.getUTCDay() || 7;
  localDate.setUTCDate(localDate.getUTCDate() + 4 - weekday);
  const isoYear = localDate.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(
    ((localDate.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7,
  );
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

export function addDaysIso(value: string | Date, days: number): string {
  const date = typeof value === "string" ? new Date(value) : new Date(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

export interface ScheduledOccurrence {
  dateKey: string;
  startsAt: string;
  endsAt: string;
  state: "scheduled" | "in_progress";
}

export function upcomingOccurrences(
  value: string | Date,
  timeZone: string,
  weekdays: number[],
  startTime: string,
  endTime: string,
  horizonDays = 56,
): ScheduledOccurrence[] {
  const now = typeof value === "string" ? new Date(value) : new Date(value);
  const startKey = businessDateKey(now, timeZone);
  const [year, month, day] = startKey.split("-").map(Number);
  const cursor = new Date(Date.UTC(year!, month! - 1, day!));
  const allowed = new Set(weekdays);
  const result: ScheduledOccurrence[] = [];
  for (let offset = 0; offset < horizonDays; offset += 1) {
    const localDate = new Date(cursor);
    localDate.setUTCDate(cursor.getUTCDate() + offset);
    if (!allowed.has(localDate.getUTCDay())) continue;
    const dateKey = `${localDate.getUTCFullYear()}-${String(localDate.getUTCMonth() + 1).padStart(2, "0")}-${String(localDate.getUTCDate()).padStart(2, "0")}`;
    const starts = zonedLocalToDate(dateKey, startTime, timeZone);
    const ends = zonedLocalToDate(dateKey, endTime, timeZone);
    if (ends.getTime() <= now.getTime()) continue;
    result.push({
      dateKey,
      startsAt: starts.toISOString(),
      endsAt: ends.toISOString(),
      state: starts.getTime() <= now.getTime() ? "in_progress" : "scheduled",
    });
  }
  return result;
}
